import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { MagicLinkStore } from '@/features/iam/magic-link/MagicLinkStore'
import { action, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { pattern, required, validationContext } from 'validx'
import type { Id } from '@/infra/id'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import { TooManyRequests } from 'fejl'

export class RequestMagicLinkViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The email address to which the magic link will be sent.
   */
  @observable
  accessor email = ''

  /**
   * Indicates whether the magic link request has been submitted.
   */
  @observable
  accessor submitted = false

  /**
   * Validation context for the view model fields.
   */
  readonly validation = validationContext(this, {
    email: [
      required('Email is required'),
      pattern('email', 'Must be a valid email'),
    ],
  })

  constructor(
    private readonly _magicLinkStore: MagicLinkStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
  ) {}

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Sends a magic link to the specified email address for the given workspace.
   */
  sendMagicLink = task.resolved(async (workspaceId: Id, email: string) => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    await this._magicLinkStore
      .request(workspaceId, email)
      .then(() => runInAction(() => (this.submitted = true)))
      .catch((e: unknown) => {
        const message =
          e instanceof TooManyRequests
            ? 'You have made too many requests. Please try again in a few minutes.'
            : 'Requesting magic link failed'

        this._createSnackbarErrorHandler(message)(e)
      })
  })

  /**
   * Resets the view model to its initial state.
   */
  @action.bound
  reset() {
    this.submitted = false
    this.validation.reset()
  }
}
